import { useEffect, useState, type FormEvent } from 'react';
import { Segmented } from '../../ui/bits';
import { FieldError } from '../payments/kit';
import { MAX_PERIOD_DAYS, manilaToday, periodLabel, samePeriod, validatePeriod, type Period } from './period.js';
import { DateField } from '../../ui/controls';
import { Icon } from '../../ui/Icon';
import '../payments/money.css';

export type PeriodPreset = { id: string; label: string; period: Period };

/**
 * Preset periods as a segmented control and the range as a quiet chip, plus "Custom" with two
 * Manila dates and a Show button (≤ 92 days, never after today). Used by Reports, Catch-weight
 * and Payouts.
 */
export function PeriodPicker({ label, presets, value, onChange, max = MAX_PERIOD_DAYS, idPrefix }: { label: string; presets: PeriodPreset[]; value: Period; onChange(p: Period): void; max?: number; idPrefix: string }) {
  const matched = presets.find((p) => samePeriod(p.period, value))?.id ?? null;
  const [custom, setCustom] = useState(matched === null);
  const [from, setFrom] = useState(value.from);
  const [to, setTo] = useState(value.to);
  const [err, setErr] = useState<{ field: 'from' | 'to'; error: string } | null>(null);

  useEffect(() => {
    setFrom(value.from);
    setTo(value.to);
  }, [value.from, value.to]);

  const pick = (id: string) => {
    setErr(null);
    if (id === 'custom') {
      setCustom(true);
      return;
    }
    const p = presets.find((x) => x.id === id);
    if (!p) return;
    setCustom(false);
    onChange(p.period);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const r = validatePeriod({ from, to }, { max, today: manilaToday() });
    if (!r.ok) {
      setErr({ field: r.field, error: r.error });
      return;
    }
    setErr(null);
    onChange(r.value);
  };

  const selected = custom ? 'custom' : (matched ?? 'custom');
  return (
    <div className="mn-period">
      <div className="mn-period-top">
        <Segmented label={label} value={selected} options={[...presets.map((p) => ({ value: p.id, label: p.label })), { value: 'custom', label: 'Custom' }]} onChange={pick} />
        <span className="mn-range" aria-live="polite" title="Manila days">
          <Icon name="calendar" />
          {periodLabel(value)}
        </span>
      </div>
      {custom ? (
        <form className="ops-period" onSubmit={submit} noValidate aria-label="Custom period">
          <div className="field">
            <label htmlFor={`${idPrefix}-from`}>From</label>
            <DateField
              id={`${idPrefix}-from`}
              className="input"
              value={from}
              max={to || undefined}
              onChange={(e) => setFrom(e.target.value)}
              aria-invalid={err?.field === 'from' ? true : undefined}
              aria-describedby={err?.field === 'from' ? `${idPrefix}-err` : undefined}
            />
          </div>
          <div className="field">
            <label htmlFor={`${idPrefix}-to`}>To</label>
            <DateField
              id={`${idPrefix}-to`}
              className="input"
              value={to}
              min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
              aria-invalid={err?.field === 'to' ? true : undefined}
              aria-describedby={err?.field === 'to' ? `${idPrefix}-err` : undefined}
            />
          </div>
          <button type="submit" className="btn btn-secondary">
            Show
          </button>
          <FieldError id={`${idPrefix}-err`}>{err?.error}</FieldError>
        </form>
      ) : null}
    </div>
  );
}
